import {Button, Select} from '@thryvlabs/maverick'
import React, {useEffect, useRef, useState} from 'react'
import {useAuthClient} from '../utils/use-auth-client'
import {ResultsLayoutContainer} from './ui/results-layout'
import Pagination from './pagination'
import {EditCouponModal} from './coupons/edit-coupon-modal'

const ALL_OPTION = {name: 'All', value: 'All'}

const DEFAULT_FILTERS = {
  country: ALL_OPTION,
  couponCode: '',
  description: '',
  sortBy: 'coupon_code',
  sortOrder: 'asc',
  page: 1,
  pageSize: 10,
}

const SORT_COLUMNS = [
  // {label: 'No.', key: null},
  {label: 'Country', key: 'country'},
  {label: 'Coupon Description', key: 'coupon_desc'},
  {label: 'Coupon Code', key: 'coupon_code'},
  {label: 'Roles', key: null},
  {label: 'End Date', key: 'end_date'},
  {label: 'Actions', key: null},
]

const TAG_CHAR_W = 7.5
const TAG_PAD = 12
const TAG_GAP = 4
const MORE_BADGE_W = 62
const ROLES_COL_W = 420

function RoleTags({roles}) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const close = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  if (!roles || roles.length === 0)
    return (
      <span style={{color: '#9ca3af', fontSize: '12px', marginLeft: '16px'}}>
        —
      </span>
    )

  let used = 0
  const visible = []
  for (let i = 0; i < roles.length; i++) {
    const w = roles[i].length * TAG_CHAR_W + TAG_PAD
    const hasMore = i < roles.length - 1
    const space = hasMore ? ROLES_COL_W - MORE_BADGE_W - TAG_GAP : ROLES_COL_W
    if (used + w + (visible.length > 0 ? TAG_GAP : 0) <= space) {
      used += w + (visible.length > 0 ? TAG_GAP : 0)
      visible.push(roles[i])
    } else break
  }
  const hidden = roles.slice(visible.length)

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        flexWrap: 'nowrap',
      }}
    >
      {visible.map(r => (
        <span
          key={r}
          style={{
            display: 'inline-block',
            background: '#f3f4f6',
            color: '#111827',
            borderRadius: '4px',
            padding: '2px 5px',
            fontSize: '11px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
          }}
        >
          {r}
        </span>
      ))}
      {hidden.length > 0 && (
        <div ref={ref} style={{position: 'relative', flexShrink: 0}}>
          <span
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              background: '#f3f4f6',
              border: '0.5px solid #d1d5db',
              borderRadius: '4px',
              padding: '2px 7px',
              fontSize: '11px',
              fontWeight: 500,
              color: '#6b7280',
              whiteSpace: 'nowrap',
              cursor: 'default',
            }}
          >
            +{hidden.length} more
          </span>
          {open && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '4px',
                background: '#fff',
                border: '0.5px solid #d1d5db',
                borderRadius: '6px',
                padding: '8px 10px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                zIndex: 100,
                width: 'max-content',
                maxWidth: '400px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4px',
              }}
            >
              {hidden.map(r => (
                <span
                  key={r}
                  style={{
                    display: 'inline-block',
                    background: '#f3f4f6',
                    color: '#111827',
                    borderRadius: '4px',
                    padding: '2px 7px',
                    fontSize: '11px',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {r}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function SortIcon({active, order}) {
  if (active && order === 'asc') {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 12 12"
        fill="none"
        style={{marginLeft: '6px', flexShrink: 0}}
      >
        <path
          d="M6 10V2M3 4l3-3 3 3"
          stroke="#FF5000"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }
  if (active && order === 'desc') {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 12 12"
        fill="none"
        style={{marginLeft: '6px', flexShrink: 0}}
      >
        <path
          d="M6 2v8M3 8l3 3 3-3"
          stroke="#FF5000"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 12 12"
      fill="none"
      style={{marginLeft: '6px', flexShrink: 0}}
    >
      <path
        d="M6 2v8M3 8l3 3 3-3"
        stroke="#c0c0c0"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CouponsConfiguration() {
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS)
  const [searchDescriptionTerm, setSearchDescriptionTerm] = useState('')
  const [searchCouponCodeTerm, setSearchCouponCodeTerm] = useState('')
  const [availableCountries, setAvailableCountries] = useState([ALL_OPTION])
  const [couponsData, setCouponsData] = useState([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [dataLoading, setDataLoading] = useState(true)
  const [searchError, setSearchError] = useState(null)
  const [editingCoupon, setEditingCoupon] = useState(null)

  const client = useAuthClient()

  useEffect(() => {
    ;(async () => {
      try {
        const res = await client('coupons/countryList')
        const raw = res?.data?.[0]?.value || ''
        const parsed = raw
          .split(',')
          .map(entry => {
            const [value, ...rest] = entry.trim().split(':')
            return {name: rest.join(':').trim(), value: value.trim()}
          })
          .filter(c => c.value && c.name)
        setAvailableCountries([ALL_OPTION, ...parsed])
      } catch {
        // keep All option if fetch fails
      }
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchCoupons = async () => {
      setDataLoading(true)
      try {
        const {
          country,
          couponCode,
          description,
          sortBy,
          sortOrder,
          page,
          pageSize,
        } = appliedFilters
        const params = new URLSearchParams()
        params.set('page', page)
        params.set('pageSize', pageSize)
        params.set('sortBy', sortBy)
        params.set('sortOrder', sortOrder)
        if (couponCode.length >= 3) params.append('couponCode', couponCode)
        if (description.length >= 3) params.append('couponName', description)
        if (country.value !== 'All') params.append('country', country.value)

        const result = await client(`coupons/listCoupons?${params.toString()}`)
        setCouponsData(Array.isArray(result?.data) ? result.data : [])
        setTotalRecords(result?.total ?? 0)
        setSearchError(null)
      } catch (err) {
        setSearchError(
          err.message || 'Failed to load coupons. Please try again.',
        )
      } finally {
        setDataLoading(false)
      }
    }

    fetchCoupons()
  }, [appliedFilters]) // eslint-disable-line react-hooks/exhaustive-deps

  const isSearchEnabled =
    appliedFilters.country.value !== 'All' ||
    searchDescriptionTerm.length >= 3 ||
    searchCouponCodeTerm.length >= 3

  const showMinCharHint =
    (searchDescriptionTerm.length > 0 && searchDescriptionTerm.length < 3) ||
    (searchCouponCodeTerm.length > 0 && searchCouponCodeTerm.length < 3)

  const handleSort = key => {
    setAppliedFilters(f => ({
      ...f,
      sortBy: key,
      sortOrder: f.sortBy === key && f.sortOrder === 'asc' ? 'desc' : 'asc',
      page: 1,
    }))
  }

  const onSearchClick = () => {
    setAppliedFilters(f => ({
      ...f,
      couponCode: searchCouponCodeTerm,
      description: searchDescriptionTerm,
      page: 1,
    }))
  }

  const onClearClick = () => {
    setSearchCouponCodeTerm('')
    setSearchDescriptionTerm('')
    setAppliedFilters(DEFAULT_FILTERS)
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          paddingInline: '20px',
          justifyContent: 'flex-end',
        }}
      >
        <form style={{display: 'flex'}} onSubmit={e => e.preventDefault()}>
          <Select
            options={availableCountries}
            width="md"
            selectLabel="Select country"
            setSelectedOption={country =>
              setAppliedFilters(f => ({...f, country, page: 1}))
            }
            selectedOption={appliedFilters.country}
            className="select-time mr-4"
          />
          <input
            type="text"
            className="input-search mr-2"
            style={{
              border: '1px solid #ededf2',
              borderRadius: '4px',
              paddingLeft: '8px',
              fontSize: '12px',
              width: '200px',
            }}
            placeholder="Search by coupon description"
            value={searchDescriptionTerm}
            onChange={e => setSearchDescriptionTerm(e.target.value)}
          />

          <input
            type="text"
            className="input-search mr-2"
            style={{
              border: '1px solid #ededf2',
              borderRadius: '4px',
              paddingLeft: '8px',
              fontSize: '12px',
              width: '180px',
            }}
            placeholder="Search by coupon code"
            value={searchCouponCodeTerm}
            onChange={e => setSearchCouponCodeTerm(e.target.value)}
          />

          <Button
            onClick={onSearchClick}
            variant="primary"
            type="submit"
            disabled={!isSearchEnabled}
          >
            Search
          </Button>
          <Button
            onClick={onClearClick}
            variant="secondary"
            type="button"
            className="ml-2"
          >
            Clear
          </Button>
        </form>
      </div>
      {showMinCharHint && (
        <div
          style={{
            margin: '6px 20px 0',
            fontSize: '12px',
            color: '#6b7280',
            textAlign: 'right',
            fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
          }}
        >
          Enter at least 3 characters to search
        </div>
      )}
      {searchError && (
        <div
          style={{
            margin: '12px 20px 0',
            padding: '10px 16px',
            backgroundColor: '#fff5f5',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            color: '#b91c1c',
            fontSize: '14px',
            fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
          }}
        >
          {searchError}
        </div>
      )}
      <ResultsLayoutContainer>
        <table
          className="table table-hover table-small"
          style={{width: '100%'}}
        >
          <thead>
            <tr>
              {SORT_COLUMNS.map(({label, key}) => (
                <th scope="col" key={label}>
                  <h6
                    className="text-thryv-steel font-semibold font-montserrat text-[18px] capitalize"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: key ? 'pointer' : 'default',
                      userSelect: 'none',
                      margin: 0,
                    }}
                    onClick={() => key && handleSort(key)}
                  >
                    {label}
                    {key && (
                      <SortIcon
                        active={appliedFilters.sortBy === key}
                        order={appliedFilters.sortOrder}
                      />
                    )}
                  </h6>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="w-100 p-3">
            {dataLoading ? (
              <tr>
                <td
                  colSpan={6}
                  style={{
                    textAlign: 'center',
                    padding: '40px 0',
                    color: '#6b7280',
                  }}
                >
                  Loading...
                </td>
              </tr>
            ) : totalRecords === 0 ? (
              <tr>
                <td colSpan={6} style={{padding: '16px 0'}}>
                  No coupons found matching your criteria.
                </td>
              </tr>
            ) : (
              couponsData?.map(
                (
                  {country, coupon_code, coupon_desc, end_date, roles},
                  index,
                ) => (
                  <tr key={`${country}-${coupon_code}-${index}`}>
                    {/* <td>{index + 1 + (appliedFilters.page - 1) * appliedFilters.pageSize}</td> */}
                    <td>{country}</td>
                    <td
                      style={{
                        maxWidth: '300px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {coupon_desc}
                    </td>
                    <td
                      style={{
                        maxWidth: '220px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {coupon_code}
                    </td>
                    <td>
                      <RoleTags roles={roles} />
                    </td>
                    <td>{end_date ? end_date.split('T')[0] : '—'}</td>
                    <td>
                      <Button
                        variant="secondary"
                        onClick={() =>
                          setEditingCoupon({
                            country,
                            couponCode: coupon_code,
                            endDate: '',
                            roles: [],
                          })
                        }
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ),
              )
            )}
          </tbody>
        </table>

        {editingCoupon && (
          <EditCouponModal
            coupon={editingCoupon}
            onClose={() => setEditingCoupon(null)}
            onSaved={() => {
              setEditingCoupon(null)
              onSearchClick()
            }}
          />
        )}
        {totalRecords > 0 && (
          <Pagination
            totalRecords={totalRecords}
            recordsPerPage={appliedFilters.pageSize}
            setRecordsPerPage={value =>
              setAppliedFilters(f => ({...f, pageSize: value, page: 1}))
            }
            currentPage={appliedFilters.page}
            setCurrentPage={pageOrUpdater =>
              setAppliedFilters(f => ({
                ...f,
                page:
                  typeof pageOrUpdater === 'function'
                    ? pageOrUpdater(f.page)
                    : pageOrUpdater,
              }))
            }
            totalPages={Math.ceil(totalRecords / appliedFilters.pageSize)}
          />
        )}
      </ResultsLayoutContainer>
    </>
  )
}

export {CouponsConfiguration}
